/** Presentation only: logo selection never determines connection capabilities. */
export function operatingSystem(platform: string, version = '') {
  const reported = platform.trim().toLowerCase();
  if (/^(windows|win32|win64)\b/.test(reported))
    return { icon: 'windows11', label: 'Windows' } as const;
  if (/^(macos|mac os|darwin|os x)\b/.test(reported))
    return { icon: 'apple', label: 'macOS' } as const;
  const description = `${reported} ${version.toLowerCase()}`;
  if (/\bubuntu\b/.test(description)) return { icon: 'ubuntu', label: 'Ubuntu' } as const;
  if (/\bdebian\b/.test(description)) return { icon: 'debian', label: 'Debian' } as const;
  if (/\bfedora\b/.test(description)) return { icon: 'fedora', label: 'Fedora' } as const;
  if (/\barch(?:\s*linux)?\b/.test(description))
    return { icon: 'archlinux', label: 'Arch Linux' } as const;
  if (/\blinux\b/.test(description)) return { icon: 'linux', label: 'Linux' } as const;
  return undefined;
}

export interface OperatingSystemDescription {
  /** Product name people recognise: "Windows 11", "macOS", "Ubuntu". */
  readonly name: string;
  /** The version as a person would say it: "15.2", "24.04", "build 26200". Empty when unknown. */
  readonly version: string;
  /** Everything the device reported, for the details page. Empty when nothing was reported. */
  readonly reported: string;
  readonly icon: NonNullable<ReturnType<typeof operatingSystem>>['icon'];
}

const macNames: Readonly<Record<number, string>> = {
  11: 'Big Sur',
  12: 'Monterey',
  13: 'Ventura',
  14: 'Sonoma',
  15: 'Sequoia',
  26: 'Tahoe',
};

/**
 * Turns what a client reports ("Windows 10.0.26200 (revision 4)", "macOS 15.2",
 * "Ubuntu 24.04") into a name and a short version people recognise. The raw string
 * stays available for the details page; nothing here changes how a device is reached.
 */
export function describeOperatingSystem(
  platform: string,
  reportedVersion = '',
): OperatingSystemDescription | undefined {
  const system = operatingSystem(platform, reportedVersion);
  if (!system) return undefined;
  const reported = reportedVersion.trim();
  const stripped = reported.replace(new RegExp(`^${system.label}\\s*`, 'i'), '').trim();
  if (system.label === 'Windows') {
    const build = /(\d+)\.(\d+)\.(\d+)/.exec(stripped);
    if (build) {
      const number = Number(build[3]);
      const name = number >= 22000 ? 'Windows 11' : number >= 10240 ? 'Windows 10' : 'Windows';
      return { name, version: `build ${number}`, reported, icon: system.icon };
    }
    const named = /^(11|10|8\.1|8|7)\b/.exec(stripped);
    return {
      name: named ? `Windows ${named[1]}` : 'Windows',
      version: named ? stripped.slice(named[0].length).trim() : stripped,
      reported,
      icon: system.icon,
    };
  }
  if (system.label === 'macOS') {
    const major = /^(\d+)/.exec(stripped);
    const codename = major ? macNames[Number(major[1])] : undefined;
    return {
      name: 'macOS',
      version: codename && stripped ? `${stripped} ${codename}` : stripped,
      reported,
      icon: system.icon,
    };
  }
  // Linux distributions report their own name; keep whatever follows it as the version.
  const withoutName = stripped.replace(/^linux\s*/i, '').trim();
  return { name: system.label, version: withoutName, reported, icon: system.icon };
}
